import useModal from "../../../@shared/components/Modal/hooks/useModal";
import ButtonPrimary from "../../../shared/Button/ButtonPrimary";
import React from "react";
import Label from "../../../components/Label/Label";
import Input from "../../../shared/Input/Input";
import { useForm } from "react-hook-form";
import { useProviders } from "../../../hooks/collections/useProviders";
import { useAlert } from "../../../@shared/hooks/useAlert";
import { useLoader } from "@shared/hooks/useLoader";
import ButtonSecondary from "../../../shared/Button/ButtonSecondary";

interface Props {
  provider?: any;
}

export const AccountProviderForm = ({ provider }: Props) => {
  const modal = useModal();
  const { createProvider, updateProvider, handleUploadFile, deleteProvider } = useProviders();
  const alert = useAlert();
  const loader = useLoader();
  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      name: provider?.name || "",
      email: provider?.email || "",
      imageUrl: provider?.imageUrl || ""
    }
  });

  const imageWatch = watch("imageUrl");

  const handleDeleteProvider = async (providerId: string) => {
    if (!await alert.confirm('Confirm', 'Are you sure you want to delete this provider?')) return;
    await deleteProvider(providerId);
    modal.close();
  };

  const inputs = [
    {
      name: 'name',
      label: 'Name',
      type: 'text'
    },
    !provider ? {
      name: 'email',
      label: 'Email',
      type: 'email'
    } : null,
    !provider ? {
      name: 'password',
      label: 'Password',
      type: 'password'
    } : null
  ].filter(Boolean);

  const submit = async (data) => {
    const isUpdate = !!provider;
    const confirmMessage = isUpdate
      ? 'Are you sure you want to update this provider?'
      : 'Are you sure you want to create this provider?';

    if (!await alert.confirm('Confirm', confirmMessage)) return;

    try {
      loader.start();

      if (isUpdate) {
        if (typeof data.imageUrl !== 'string') {
          await handleUploadFile(`users/${provider.id}`, provider.id, data.imageUrl);
        }
        await updateProvider(provider.id, { name: data.name });
        alert.toastSuccess('Provider updated successfully');
      } else {
        const providerId = await createProvider(data);
        alert.toastSuccess('Provider created successfully');
        modal.close({ providerId });
      }

      modal.close();
    } catch (e) {
      console.error(e);
      alert.error('Error', `An error occurred while ${isUpdate ? 'updating' : 'creating'} the provider`);
    } finally {
      loader.stop();
    }
  };

  return (
    <div className={'mt-4 flex gap-4 flex-col'}>
      {inputs.map((input) => (
        <div>
          <Label>{input.label}</Label>
          <Input type={input.type} className="mt-1.5" {...register(input.name as any)}/>
        </div>
      ))}

      <div>
        <Label>Photo</Label>
        <div className="dropzone mt-2 border border-dashed p-4 text-center relative">
          <input
            type="file"
            className="absolute inset-0 opacity-0 cursor-pointer"
            {...register("imageUrl")}
          />
          <p>Drop files here or click to upload</p>
        </div>

        {imageWatch && (
          <div className="mt-2">
            <img
              src={typeof imageWatch === 'string' ? imageWatch : URL.createObjectURL(imageWatch[0])}
              alt="Preview"
              className="size-32 object-cover"
            />
          </div>
        )}
      </div>

      <div className="p-4 flex items-center justify-end gap-3">
        {provider &&
          <ButtonSecondary type={"button"} onClick={() => handleDeleteProvider(provider.id)}>Delete</ButtonSecondary>
        }
        <ButtonPrimary onClick={handleSubmit(submit)}>Submit</ButtonPrimary>
      </div>
    </div>
  );
};
